import { Box, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CaseStudyContentDto } from 'tdc-web-backend/case-studies-content/schemas';
import { CaseStudyReviewDto } from 'tdc-web-backend/case-studies-review/schemas';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import CustomButton from '../../../components/button/CustomButton';
import { useRefresh } from '../../../utils/hooks/crud-hooks/useRefresh';
import useReferenceOne from '../../../utils/hooks/crud-hooks/useReferenceOne';
import useUpdate from '../../../utils/hooks/crud-hooks/useUpdate';
import { stripTags } from '../../../utils/helpers';
import CustomReferenceField from '../../../components/custom-fields/CustomReferenceField';
import ClientReview from '../../../components/client-reviews/ClientReview';
import BaseInputLabel from '../../../components/custom-inputs/BaseInputLabel';
import CaseStudyEmailLogs from './components/CaseStudyReviewEmailLogs/CaseStudyReviewEmailLogs';

type Props = {};

const CaseStudyReviewDetails = (props: Props) => {
  const { reviewId } = useParams();
  const refresh = useRefresh();
  const navigate = useNavigate();

  const { data } = useReferenceOne<CaseStudyReviewDto>({
    id: reviewId,
    resource: 'case-studies-review',
    enabled: !!reviewId,
  });

  const { mutate: approve, isLoading } = useUpdate({ resource: '/case-studies-review' });

  const handleCaseStudyReview = () => {
    approve(
      {
        id: reviewId as string,
        suffix: 'approve',
      },
      {
        onSuccess: () => {
          refresh();
          navigate('../');
        },
      },
    );
  };

  return (
    <Stack p={6} spacing={4}>
      <CustomReferenceField
        resource="case-studies-content"
        id={data?.caseStudyContent}
        displayName="title"
        renderComponent={(value: CaseStudyContentDto) => (
          <Typography variant="heading3" sx={{ color: 'primaryDark.600' }}>
            {value?.title}
          </Typography>
        )}
      />
      <Stack direction="row" gap={4} pt={4} alignItems="center">
        <CustomButton
          disabled={!(!data?.adminApproved && data?.requestPublish)}
          loading={isLoading}
          onClick={() => handleCaseStudyReview()}
        >
          Approve
        </CustomButton>
      </Stack>
      <Stack spacing={1} pt={3}>
        <Typography>Client info</Typography>

        <Grid container rowSpacing={4} spacing={2} gridAutoRows="max-content" sx={{ pb: 4 }}>
          <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
            <BaseInputLabel text="name" />
            <Typography>
              {data?.reviewer?.firstName} {data?.reviewer?.lastName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
            <BaseInputLabel text="company name" />
            <Typography>{data?.reviewer?.companyName}</Typography>
          </Grid>

          <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
            <BaseInputLabel text="company website" />
            <Typography>{data?.reviewer?.companyWebsite}</Typography>
          </Grid>

          <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
            <BaseInputLabel text="job title" />
            <Typography>{data?.reviewer?.jobTitle}</Typography>
          </Grid>

          <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
            <BaseInputLabel text="e-mail" />
            <Typography>{data?.reviewer?.email}</Typography>
          </Grid>
        </Grid>

        <Divider />
      </Stack>
      {data?.reviewerLinkedin && (
        <Stack spacing={1}>
          <Typography>LinkedIn Data</Typography>

          <Grid container rowSpacing={4} spacing={2} gridAutoRows="max-content" sx={{ pb: 4 }}>
            <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
              <BaseInputLabel text="name" />
              <Typography>
                {data?.reviewerLinkedin?.given_name} {data?.reviewerLinkedin?.family_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
              <BaseInputLabel text="e-mail" />
              <Typography>{data?.reviewerLinkedin?.email}</Typography>
            </Grid>

            <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
              <BaseInputLabel text="email verified" />
              {data?.reviewerLinkedin?.email_verified ? <CheckIcon /> : <ClearIcon />}
            </Grid>

            <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
              <BaseInputLabel text="sub" />
              <Typography>{data?.reviewerLinkedin?.sub}</Typography>
            </Grid>

            <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
              <BaseInputLabel text="local" />
              <Typography>{data?.reviewerLinkedin?.locale}</Typography>
            </Grid>
            <Grid item xs={12} md={4} xl={3} xxl={2} gridAutoRows="max-content">
              {data?.reviewerLinkedin?.picture && (
                <Link href={data?.reviewerLinkedin?.picture} target="_blank">
                  <Box component="img" src={data?.reviewerLinkedin?.picture} height="5rem" />
                </Link>
              )}
            </Grid>
          </Grid>

          <Divider />
        </Stack>
      )}
      {data?.requestPublish ? (
        <Stack spacing={3}>
          <Stack>
            <Typography
              color="primaryDark.500"
              variant="publicBody5Regular"
              sx={{ mr: 3, textTransform: 'none' }}
            >
              Quote
            </Typography>

            <Typography mb={1.5} color="primaryDark.600" variant="body3">
              {data?.summarization}
            </Typography>
          </Stack>

          <ClientReview
            title="Quality"
            description={stripTags(data?.score.qualityText as string)}
            rating={data?.score.qualityScore ?? 0}
            detailedView
          />
          <ClientReview
            title="Budget"
            description={stripTags(data?.score.budgetText as string)}
            rating={data?.score.budgetScore ?? 0}
            detailedView
          />
          <ClientReview
            title="Schedule"
            description={stripTags(data?.score.scheduleText as string)}
            rating={data?.score.scheduleScore ?? 0}
            detailedView
          />
          <ClientReview
            title="Willingness to refer"
            description={stripTags(data?.score.recommendationText as string)}
            rating={data?.score.recommendationScore ?? 0}
            detailedView
          />
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: 'primaryDark.600', pt: 2 }}>
          The client hasn't submitted the review yet.
        </Typography>
      )}
      <Divider />
      {data && (
        <CaseStudyEmailLogs reviewId={reviewId as string} requestPublish={data?.requestPublish} />
      )}
    </Stack>
  );
};

export default CaseStudyReviewDetails;
